import { getFlagPath } from "./FlagUtils.js";

/**
 * Préchargement des drapeaux.
 *
 * Les jeux attendaient autrefois les 193 drapeaux (environ 2,2 Mo) avant la
 * première question : plus de 15 s en 4G lente, sur la toute première visite,
 * celle qui décide si un nouveau joueur reste. Désormais :
 * - `start()` charge tous les drapeaux en fond, quelques-uns à la fois, pour que
 *   les questions suivantes soient prêtes sans bloquer la page ;
 * - `load(codes)` passe devant la file : c'est ce qu'un jeu attend avant
 *   d'afficher une question (un seul drapeau, quelques centaines de ms) ;
 * - `prioritize(codes)` réordonne la file quand l'ordre des questions est
 *   connu d'avance (Speed Run).
 * Le fond se met en pause tant qu'un drapeau prioritaire est en route, pour lui
 * laisser la bande passante.
 */

const CONCURRENCY = 6;

const requests = new Map(); // code → Promise<boolean>
const loadedCodes = new Set();
let queue = [];
let active = 0;
let priorityInFlight = 0;
let started = false;
let total = 0;
let allPromise = null;
let resolveAll = null;

function loadImage(code) {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = getFlagPath(code);
    });
}

/** Une requête par drapeau, avec une seule nouvelle tentative (réseau transitoire). */
function request(code) {
    if (requests.has(code)) return requests.get(code);
    const promise = loadImage(code)
        .then(ok => ok || loadImage(code))
        .then(ok => {
            if (ok) loadedCodes.add(code);
            else requests.delete(code); // un prochain `load()` pourra réessayer
            return ok;
        });
    requests.set(code, promise);
    return promise;
}

function settleAllIfDone() {
    if (resolveAll && queue.length === 0 && active === 0) {
        resolveAll();
        resolveAll = null;
    }
}

function pump() {
    while (active < CONCURRENCY && priorityInFlight === 0 && queue.length) {
        const code = queue.shift();
        if (requests.has(code)) continue;
        active++;
        request(code).finally(() => {
            active--;
            pump();
            settleAllIfDone();
        });
    }
    settleAllIfDone();
}

/** Lance le préchargement de fond de tous les drapeaux (une seule fois). */
export function start(countries) {
    if (started) return;
    started = true;
    total = countries.length;
    allPromise = new Promise(resolve => { resolveAll = resolve; });
    queue = countries.map(c => c.code).filter(code => !requests.has(code));
    pump();
}

/**
 * Charge ces drapeaux en priorité et résout quand ils sont prêts (ou en échec :
 * mieux vaut une question sans image qu'une partie bloquée).
 */
export function load(codes) {
    const pending = codes.filter(code => !loadedCodes.has(code));
    if (!pending.length) return Promise.resolve();
    queue = queue.filter(code => !pending.includes(code));
    priorityInFlight++;
    return Promise.all(pending.map(request)).then(() => {}).finally(() => {
        priorityInFlight--;
        pump();
    });
}

/** Place ces drapeaux en tête de la file de fond, dans cet ordre. */
export function prioritize(codes) {
    const wanted = codes.filter(code => !requests.has(code));
    const rest = queue.filter(code => !wanted.includes(code));
    queue = [...wanted, ...rest];
}

/** Vrai si tous ces drapeaux sont déjà en cache. */
export function areLoaded(codes) {
    return codes.every(code => loadedCodes.has(code));
}

/**
 * Le préchargement tourne-t-il sur cette page ? Sinon (tests, page sans
 * données), les jeux affichent leurs questions sans attendre.
 */
export function isActive() {
    return started;
}

/** Attend que tous les drapeaux soient dans le cache du navigateur. */
export async function wait() {
    if (allPromise) await allPromise;
}

export function isReady() {
    return total > 0 && loadedCodes.size >= total;
}

/** Remise à zéro, pour les tests. */
export function _reset() {
    requests.clear();
    loadedCodes.clear();
    queue = [];
    active = 0;
    priorityInFlight = 0;
    started = false;
    total = 0;
    allPromise = null;
    resolveAll = null;
}
